import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiPackage,
    FiTruck,
    FiCheckCircle,
    FiAlertCircle,
    FiTrendingUp,
    FiClock,
    FiMapPin,
    FiUser,
    FiZap,
    FiAward,
    FiShield,
    FiActivity,
    FiDollarSign
} from "react-icons/fi";
import { FLOOR_HEADS, FLOOR_MANAGERS, MANAGER_META } from "../json/salesTarget";
import { apiurl } from "../config/config";

/* ================= TV SCALE HOOK ================= */
function useTVScale() {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const updateScale = () => {
            const baseWidth = 1920;
            setScale(window.innerWidth / baseWidth);
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);
    return scale;
}

/* ================= ANIMATED NUMBER ================= */
function Counter({ value, suffix = "" }) {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let start = displayValue;
        const end = parseInt(value) || 0;
        if (start === end) return;

        let totalDuration = 1000;
        let startTime = null;

        function animate(currentTime) {
            if (!startTime) startTime = currentTime;
            const progress = Math.min((currentTime - startTime) / totalDuration, 1);
            const current = Math.floor(progress * (end - start) + start);
            setDisplayValue(current);
            if (progress < 1) requestAnimationFrame(animate);
        }

        requestAnimationFrame(animate);
    }, [value]);

    return <span>{displayValue.toLocaleString()}{suffix}</span>;
}

/* ================= STYLES ================= */
const GLASS_CARD_DARK = "bg-[#0a0a0c]/80 backdrop-blur-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5),inset_0_1px_1px_rgba(255,255,255,0.1)] rounded-[32px]";
const GLASS_CARD_LIGHT = "bg-white border border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.05)] rounded-[32px]";
const GRADIENT_BG_DARK = "bg-[#010103] text-white overflow-hidden font-['Inter',sans-serif]";
const GRADIENT_BG_LIGHT = "bg-slate-50 text-slate-900 overflow-hidden font-['Inter',sans-serif]";

/* ================= HELPERS ================= */
const formatCurrency = (val) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);

const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s?.includes("delivered")) return "text-emerald-400";
    if (s?.includes("rto")) return "text-rose-400";
    if (s?.includes("verified")) return "text-sky-400";
    if (s?.includes("shipped")) return "text-amber-400";
    return "text-indigo-400";
};

const isValidOrder = (o) => {
    return !o.status_name?.toLowerCase().includes("cancel");
};

/* ================= COMPONENTS ================= */

const StatCard = ({ title, value, icon: Icon, color, delay, isDark }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className={`${isDark ? GLASS_CARD_DARK : GLASS_CARD_LIGHT} p-6 flex flex-col items-center justify-center text-center relative group overflow-hidden`}
    >
        {isDark && (
            <div className={`absolute top-0 right-0 w-32 h-32 ${color.replace('text', 'bg')}/10 blur-[60px] -mr-16 -mt-16`} />
        )}
        <div className={`p-4 rounded-2xl ${isDark ? 'bg-white/5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]' : 'bg-slate-100 shadow-sm'} mb-3 ${color} relative z-10`}>
            <Icon size={40} />
        </div>
        <h3 className={`text-sm font-bold ${isDark ? 'text-white/40' : 'text-slate-400'} mb-1 tracking-widest uppercase relative z-10`}>{title}</h3>
        <div className={`text-6xl font-black tracking-tighter relative z-10 ${isDark ? 'text-white' : 'text-slate-900'} ${isDark ? 'drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]' : ''}`}>
            <Counter value={value} />
        </div>
        {isDark && (
            <motion.div
                animate={{ opacity: [0.1, 0.2, 0.1] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"
            />
        )}
    </motion.div>
);

const AgentRankCard = ({ agent, rank, isLow = false, isDark }) => (
    <motion.div
        initial={{ opacity: 0, x: isLow ? 50 : -50 }}
        whileInView={{ opacity: 1, x: 0 }}
        className={`${isDark ? 'bg-[#121214]/60 border-white/5 shadow-[0_4px_20px_rgba(0,0,0,0.3)]' : 'bg-white border-slate-200 shadow-sm'} p-4 rounded-2xl border flex items-center gap-4 group hover:bg-indigo-500/5 transition-all relative overflow-hidden`}
    >
        {isDark && <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-indigo-500/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />}
        <div className={`w-10 h-10 flex items-center justify-center rounded-xl font-black text-lg ${rank <= 3 ? (isDark ? 'bg-indigo-600 shadow-[0_0_15px_rgba(79,70,229,0.4)]' : 'bg-indigo-600 shadow-md') + ' text-white' : (isDark ? 'bg-white/5 text-white/40' : 'bg-slate-100 text-slate-400')}`}>
            #{rank}
        </div>
        <div className="flex-1 overflow-hidden">
            <div className={`font-black text-base ${isDark ? 'text-white/90' : 'text-slate-800'} group-hover:text-indigo-400 transition-colors truncate`}>{agent.name}</div>
            <div className={`text-[8px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase tracking-widest truncate`}>{agent.manager}</div>
        </div>
        <div className="text-right">
            <div className={`text-xl font-black ${isDark ? 'text-indigo-400 shadow-[0_0_10px_rgba(129,140,248,0.2)]' : 'text-indigo-600'} leading-none`}>{agent.daily}</div>
            <div className={`text-[8px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase`}>Orders</div>
        </div>
        <div className={`w-14 text-right border-l ${isDark ? 'border-white/5' : 'border-slate-200'} pl-2`}>
            <div className={`text-base font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'} leading-none`}>{agent.monthly}</div>
            <div className={`text-[8px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase`}>MTD</div>
        </div>
    </motion.div>
);

const FloorHeadCard = ({ head, stats, isDark }) => (
    <motion.div
        className={`${isDark ? GLASS_CARD_DARK : GLASS_CARD_LIGHT} p-8 flex flex-col items-center relative overflow-hidden group`}
        whileHover={{ scale: 1.01 }}
    >
        {isDark && (
            <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.03] to-transparent pointer-events-none" />
        )}
        <div className="relative mb-8">
            <div className={`absolute inset-0 rounded-full blur-2xl animate-pulse ${isDark ? 'bg-indigo-500/20' : 'bg-indigo-500/5'}`} />
            <img src={head.photo} alt={head.name} className={`w-[180px] h-[180px] rounded-full object-cover border-[8px] relative z-10 shadow-2xl ${isDark ? 'border-white/5 shadow-indigo-500/20' : 'border-white shadow-slate-200'}`} />
        </div>

        <h2 className={`text-5xl font-black mb-1 tracking-tighter leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>{head.name}</h2>
        <div className={`flex items-center gap-3 mb-8 font-black uppercase tracking-[0.3em] text-sm px-5 py-1.5 rounded-full border shadow-inner ${isDark ? 'text-white/40 bg-white/5 border-white/10' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
            <FiShield className="text-indigo-500" /> <span>FLOOR COMMANDER</span>
        </div>

        <div className="grid grid-cols-2 gap-4 w-full mb-8 relative z-10">
            <div className={`${isDark ? 'bg-white/[0.03] border-white/5 shadow-inner' : 'bg-slate-50 border-slate-200 shadow-sm'} p-4 rounded-2xl border text-center`}>
                <div className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mb-1`}>Today Orders</div>
                <div className={`text-4xl font-black ${isDark ? 'text-indigo-400' : 'text-indigo-600'} tracking-tighter`}><Counter value={stats.daily.orders} /></div>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03] border-white/5 shadow-inner' : 'bg-slate-50 border-slate-200 shadow-sm'} p-4 rounded-2xl border text-center`}>
                <div className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mb-1`}>Monthly Orders</div>
                <div className={`text-4xl font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'} tracking-tighter`}><Counter value={stats.monthly.orders} /></div>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03] border-white/5 shadow-inner' : 'bg-slate-50 border-slate-200 shadow-sm'} p-4 rounded-2xl border text-center`}>
                <div className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mb-1`}>Today Sales</div>
                <div className={`text-2xl font-black ${isDark ? 'text-indigo-400' : 'text-indigo-600'} tracking-tighter`}>{formatCurrency(stats.daily.sales)}</div>
            </div>
            <div className={`${isDark ? 'bg-white/[0.03] border-white/5 shadow-inner' : 'bg-slate-50 border-slate-200 shadow-sm'} p-4 rounded-2xl border text-center`}>
                <div className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mb-1`}>Monthly Sales</div>
                <div className={`text-2xl font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'} tracking-tighter`}>{formatCurrency(stats.monthly.sales)}</div>
            </div>
        </div>

        <div className={`w-full space-y-4 pt-4 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
            <div className="flex justify-between items-center">
                <div className="flex flex-col">
                    <span className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase`}>Delivered Orders</span>
                    <span className={`text-2xl font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'} leading-none`}>{stats.monthly.deliveredOrders}</span>
                </div>
                <div className="text-right">
                    <span className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase`}>Delivered Amount</span>
                    <div className={`text-2xl font-black ${isDark ? 'text-emerald-400' : 'text-emerald-600'} leading-none`}>{formatCurrency(stats.monthly.deliveredSales)}</div>
                </div>
            </div>
            <div className={`w-full h-2.5 rounded-full p-0.5 border ${isDark ? 'bg-white/5 border-white/5 shadow-inner' : 'bg-slate-100 border-slate-200 shadow-sm'}`}>
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, (stats.daily.orders / head.target) * 100)}%` }}
                    className={`h-full rounded-full ${isDark ? 'bg-gradient-to-r from-indigo-600 to-indigo-400 shadow-[0_0_15px_rgba(99,102,241,0.5)]' : 'bg-indigo-600 shadow-md'}`}
                />
            </div>
        </div>
    </motion.div>
);


const ManagerCompactCard = ({ name, daily, monthly, delay, color, isDark }) => {
    const meta = MANAGER_META[name] || { photo: "/managers/default.png" };
    const accent = color === "amber" ? (isDark ? "text-amber-400" : "text-amber-600") : (isDark ? "text-sky-400" : "text-sky-600");
    const bg = color === "amber" ? (isDark ? "bg-amber-500/[0.03] border-amber-500/10" : "bg-amber-50 border-amber-200") : (isDark ? "bg-sky-500/[0.03] border-sky-500/10" : "bg-sky-50 border-sky-200");

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay }}
            className={`${bg} border p-4 rounded-2xl flex items-center gap-4 hover:brightness-110 transition-all group shadow-sm relative overflow-hidden`}
        >
            {isDark && <div className={`absolute top-0 right-0 w-16 h-16 ${color === "amber" ? "bg-amber-500" : "bg-sky-500"}/10 blur-2xl`} />}
            <div className="relative">
                <img src={meta.photo} className={`w-14 h-14 rounded-full object-cover border-2 ${isDark ? 'border-white/10 shadow-[0_0_15px_rgba(255,255,255,0.05)]' : 'border-white shadow-sm'}`} alt={name} />
                <div className={`absolute -bottom-1 -right-1 w-5 h-5 ${color === "amber" ? "bg-amber-400" : "bg-sky-400"} rounded-full border-2 ${isDark ? "border-[#010103]" : "border-white"} flex items-center justify-center shadow-lg`}>
                    <FiUser size={10} className={isDark ? "text-[#010103]" : "text-white"} />
                </div>
            </div>

            <div className="flex-1 overflow-hidden relative z-10">
                <div className={`font-black text-lg truncate leading-none mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{name}</div>
                <div className={`text-[9px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase tracking-widest`}>Active Commander</div>
            </div>

            <div className="flex gap-4 items-center relative z-10">
                <div className="text-center">
                    <div className={`text-xl font-black ${accent} leading-none drop-shadow-sm`}>{daily.orders}</div>
                    <div className={`text-[7px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mt-1`}>Today</div>
                </div>
                <div className={`w-px h-6 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`} />
                <div className="text-center">
                    <div className={`text-xl font-black leading-none ${isDark ? 'text-white/90' : 'text-slate-700'}`}>{monthly.orders}</div>
                    <div className={`text-[7px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mt-1`}>MTD</div>
                </div>
                <div className={`w-px h-6 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`} />
                <div className="text-center">
                    <div className={`text-xl font-black leading-none ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{monthly.deliveredOrders || 0}</div>
                    <div className={`text-[7px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mt-1`}>Delivered</div>
                </div>
                <div className={`w-px h-6 ${isDark ? 'bg-white/5' : 'bg-slate-200'}`} />
                <div className="text-center">
                    <div className={`text-xl font-black leading-none ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatCurrency(monthly.deliveredSales || 0)}</div>
                    <div className={`text-[7px] ${isDark ? 'text-white/20' : 'text-slate-400'} font-black uppercase mt-1`}>Amount</div>
                </div>
            </div>
        </motion.div>
    );
};

/* ================= MAIN COMPONENT ================= */

export default function Nexus() {
    const scale = useTVScale();
    const [dailyOrders, setDailyOrders] = useState([]);
    const [monthlyOrders, setMonthlyOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [slide, setSlide] = useState("SUMMARY");
    const [lastSyncTime, setLastSyncTime] = useState("---");
    const [theme, setTheme] = useState("dark");

    const isDark = theme === "dark";

    const initialized = React.useRef(false);

    const fetchData = async () => {
        try {
            const now = new Date();
            const todayStr = now.toISOString().split("T")[0];
            const fromToday = `${todayStr} 00:00:00`;
            const toToday = `${todayStr} 23:59:59`;
            const monthStart = `${todayStr.split('-')[0]}-${todayStr.split('-')[1]}-01 00:00:00`;

            const [dailyRes, monthlyRes] = await Promise.all([
                axios.get(`${apiurl}/utils/crm/getOrderDetails`, {
                    params: { from_date: fromToday, to_date: toToday },
                    headers: { token: "ad2355a9d54335e27e2c3a3ed5b6e381" }
                }),
                axios.get(`${apiurl}/utils/crm/getOrderDetails`, {
                    params: { from_date: monthStart, to_date: toToday },
                    headers: { token: "ad2355a9d54335e27e2c3a3ed5b6e381" }
                })
            ]);

            if (dailyRes.data?.data) setDailyOrders(dailyRes.data.data);
            if (monthlyRes.data?.data) setMonthlyOrders(monthlyRes.data.data);
            setLastSyncTime(new Date().toLocaleTimeString());

        } catch (err) {
            console.error("Error fetching orders:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!initialized.current) {
            fetchData();
            initialized.current = true;
        }
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const slideInterval = setInterval(() => {
            setSlide((prev) => {
                const order = ["SUMMARY", "BATTLE", "RAHUL_TOP", "RAHUL_LOW", "ANAND_TOP", "ANAND_LOW", "MATRIX"];
                const idx = order.indexOf(prev);
                if (idx === -1) return "SUMMARY";
                return order[(idx + 1) % order.length];
            });
        }, 20000);
        return () => clearInterval(slideInterval);
    }, []);

    const stats = useMemo(() => {
        const process = (data) => {
            const res = {
                totalAll: 0,
                amountAll: 0,
                canceled: { count: 0, amount: 0 },
                total: 0,
                amount: 0,
                partial: { count: 0, amount: 0 },
                cod: { count: 0, amount: 0 },
                managers: {},
                agents: {},
                statusCounts: {},
                status: { delivered: 0, rto: 0, verified: 0, deliveredAmt: 0 }
            };
            data.forEach(o => {
                const netAmt = parseFloat(o.net_amt || 0);
                const partialAmt = parseFloat(o.partial_payment || 0);
                const orderValue = netAmt + partialAmt;
                const isPartial = partialAmt > 0 && o.payment_confirm_id !== null && o.payment_confirm_id !== "";
                const isCanceled = o.status_name?.toLowerCase().includes("cancel");

                const sName = o.status_name || "Unknown";
                res.statusCounts[sName] = (res.statusCounts[sName] || 0) + 1;

                res.totalAll++;
                res.amountAll += orderValue;

                if (isCanceled) {
                    res.canceled.count++;
                    res.canceled.amount += orderValue;
                    return; // Skip canceled for Prepaid stats
                }

                if (!isPartial) {
                    res.cod.count++;
                    res.cod.amount += orderValue;
                    return; // Skip COD orders for all other Prepaid-specific dashboard stats
                }

                res.total++;
                res.amount += orderValue;
                res.partial.count++;
                res.partial.amount += orderValue;

                const m = o.o_manager_name || "Web";
                const a = o.order_booked_by_name || "Unknown";

                if (!res.managers[m]) res.managers[m] = { orders: 0, sales: 0, deliveredOrders: 0, deliveredSales: 0 };
                res.managers[m].orders++;
                res.managers[m].sales += orderValue;

                if (!res.agents[a]) res.agents[a] = { daily: 0, manager: m };
                res.agents[a].daily++;

                const s = o.status_name?.toLowerCase() || "";
                if (s.includes("delivered")) {
                    res.status.delivered++;
                    res.status.deliveredAmt += orderValue;
                    res.managers[m].deliveredOrders++;
                    res.managers[m].deliveredSales += orderValue;
                }
                if (s.includes("rto")) res.status.rto++;
                if (s.includes("verified")) res.status.verified++;
            });
            return res;
        }
        const daily = process(dailyOrders);
        const monthlyRaw = process(monthlyOrders);

        Object.keys(monthlyRaw.agents).forEach(a => {
            if (daily.agents[a]) {
                daily.agents[a].monthly = monthlyRaw.agents[a].daily;
            } else {
                daily.agents[a] = { daily: 0, manager: monthlyRaw.agents[a].manager, monthly: monthlyRaw.agents[a].daily };
            }
        });

        return { daily, monthly: monthlyRaw };
    }, [dailyOrders, monthlyOrders]);

    const floorStats = useMemo(() => {
        const calculate = (managers, source) => {
            let o = 0; let s = 0; let dO = 0; let dS = 0;
            managers.forEach(k => {
                const m = source.managers[k] || { orders: 0, sales: 0, deliveredOrders: 0, deliveredSales: 0 };
                o += m.orders;
                s += m.sales;
                dO += m.deliveredOrders;
                dS += m.deliveredSales;
            });
            return { orders: o, sales: s, deliveredOrders: dO, deliveredSales: dS };
        }
        return {
            GMIT: { daily: calculate(FLOOR_MANAGERS.GMIT, stats.daily), monthly: calculate(FLOOR_MANAGERS.GMIT, stats.monthly) },
            ALTF: { daily: calculate(FLOOR_MANAGERS.ALTF, stats.daily), monthly: calculate(FLOOR_MANAGERS.ALTF, stats.monthly) }
        };
    }, [stats, FLOOR_MANAGERS]);

    const agentRankings = useMemo(() => {
        const getSorted = (floorKey) => {
            const agents = [];
            const floorManagers = FLOOR_MANAGERS[floorKey];
            Object.keys(stats.daily.agents).forEach(name => {
                const info = stats.daily.agents[name];
                if (floorManagers.includes(info.manager)) {
                    agents.push({
                        name,
                        manager: info.manager,
                        daily: info.daily || 0,
                        monthly: info.monthly || 0
                    });
                }
            });
            return agents.sort((a, b) => b.daily - a.daily);
        };

        const rahul = getSorted("GMIT");
        const anand = getSorted("ALTF");

        return {
            RAHUL: { top: rahul.slice(0, 20), low: rahul.slice(-20) },
            ANAND: { top: anand.slice(0, 20), low: anand.slice(-20) }
        };
    }, [stats]);

    if (loading && dailyOrders.length === 0) {
        return (
            <div className={`w-full h-screen flex flex-col items-center justify-center ${isDark ? GRADIENT_BG_DARK : GRADIENT_BG_LIGHT}`}>
                <motion.div
                    animate={{ scale: [0.9, 1, 0.9], opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 4 }}
                    className="flex flex-col items-center gap-10"
                >
                    <FiActivity size={80} className="text-indigo-500" />
                    <h2 className="text-3xl font-black italic tracking-[0.4em] text-indigo-500 uppercase">Nexus Command Syncing...</h2>
                </motion.div>
            </div>
        );
    }

    return (
        <div
            style={{
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                width: 1920,
                height: 1080,
            }}
            className={isDark ? GRADIENT_BG_DARK : GRADIENT_BG_LIGHT}
        >
            <div className={`absolute inset-0 pointer-events-none ${isDark ? "bg-[radial-gradient(circle_at_50%_0%,rgba(79,70,229,0.1),transparent_70%)]" : "bg-[radial-gradient(circle_at_50%_0%,rgba(79,70,229,0.05),transparent_70%)]"}`} />
            <div className={`absolute inset-0 pointer-events-none ${isDark ? "bg-gradient-to-br from-indigo-900/10 via-transparent to-rose-900/10" : "bg-gradient-to-br from-indigo-500/5 via-transparent to-slate-200/5"}`} />

            <div className="relative z-10 p-12 h-full flex flex-col">
                {/* Header HUD */}
                <header className={`flex justify-between items-center mb-8 border-b pb-8 ${isDark ? "border-white/10 shadow-[0_1px_15px_rgba(0,0,0,0.2)]" : "border-slate-200 shadow-sm"}`}>
                    <div className="flex items-center gap-8">
                        <div className={`w-20 h-20 flex items-center justify-center rounded-[24px] shadow-2xl border-2 ${isDark ? "bg-indigo-600 border-white/20 shadow-indigo-500/20" : "bg-white border-slate-100 shadow-slate-200"}`}>
                            <img src="/logo.png" className="w-12 h-12 object-contain" alt="Logo" onError={(e) => e.target.style.display = 'none'} />
                            <FiZap size={40} className={isDark ? "text-white animate-pulse" : "text-indigo-600 animate-pulse absolute"} />
                        </div>
                        <div>
                            <motion.h1 className={`text-5xl font-black tracking-tighter italic leading-none ${isDark ? 'drop-shadow-lg' : ''}`}>NEXUS <span className="text-indigo-500 not-italic uppercase">Operational Command</span></motion.h1>
                            <div className="flex items-center gap-4 mt-3">
                                <div className={`flex items-center gap-2 font-black px-4 py-1.5 rounded-xl text-[10px] border uppercase tracking-widest shadow-inner ${isDark ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" : "text-emerald-600 bg-emerald-50 border-emerald-200"}`}>
                                    <FiActivity size={14} /> Prepaid Node Sync
                                </div>
                                <div className={`${isDark ? "text-white/20" : "text-slate-400"} font-black uppercase tracking-[0.4em] text-[8px]`}>Daily Flux: {stats.daily.total} Nodes</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-10">
                        <button
                            onClick={() => setTheme(prev => prev === "dark" ? "light" : "dark")}
                            className={`p-3 rounded-xl border transition-all shadow-lg ${isDark ? "bg-white/5 border-white/10 text-white hover:bg-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                        >
                            {isDark ? <FiZap size={20} className="text-amber-400" /> : <FiClock size={20} className="text-indigo-600" />}
                        </button>
                        <div className="flex items-center gap-8">
                            <div className="text-right">
                                <div className={`${isDark ? "text-white/20" : "text-slate-400"} text-[8px] font-black uppercase tracking-[0.4em] mb-1 italic`}>MTD Delivered Orders</div>
                                <div className={`text-6xl font-black leading-none tracking-tighter flex items-center justify-end ${isDark ? "text-emerald-400 drop-shadow-[0_0_15px_rgba(52,211,153,0.3)]" : "text-emerald-600"}`}>
                                    <Counter value={stats.monthly.status.delivered} />
                                </div>
                            </div>
                            <div className="text-right">
                                <div className={`${isDark ? "text-white/20" : "text-slate-400"} text-[8px] font-black uppercase tracking-[0.4em] mb-1 italic`}>MTD Delivered Sales</div>
                                <div className={`text-6xl font-black leading-none tracking-tighter flex items-center justify-end ${isDark ? "text-emerald-400 drop-shadow-[0_0_15px_rgba(52,211,153,0.3)]" : "text-emerald-600"}`}>
                                    <span className="text-3xl mr-2 opacity-50 font-normal">₹</span>
                                    {formatCurrency(stats.monthly.status.deliveredAmt).replace('₹', '')}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Dynamic Viewport */}
                <div className="flex-1 relative overflow-hidden">
                    <AnimatePresence mode="wait">
                        {slide === "SUMMARY" && (
                            <motion.div key="summary" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05 }} className="h-full grid grid-cols-2 gap-10">
                                {/* MTD Card */}
                                <motion.div className={`${isDark ? GLASS_CARD_DARK : GLASS_CARD_LIGHT} p-12 flex flex-col relative overflow-hidden`}>
                                    <div className="absolute top-0 left-0 w-2 h-full bg-indigo-600" />
                                    <div className="flex justify-between items-start mb-10 z-10">
                                        <div>
                                            <h2 className={`text-6xl font-black uppercase tracking-tighter italic ${isDark ? 'text-white' : 'text-slate-900'}`}>MTD <span className="text-indigo-500 not-italic">Overview</span></h2>
                                            <p className={`text-sm font-black uppercase tracking-[0.4em] mt-3 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                                {new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} - {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </p>
                                        </div>
                                        <div className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                                            <FiActivity size={40} className="text-indigo-500" />
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col justify-center gap-8 z-10">
                                        <div className="grid grid-cols-3 gap-6">
                                            {/* Total Orders Box */}
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>Total Orders (All)</div>
                                                <div className={`text-6xl font-black tracking-tighter mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}><Counter value={stats.monthly.totalAll} /></div>
                                                <div className="text-xl font-black text-indigo-400">{formatCurrency(stats.monthly.amountAll)}</div>
                                            </div>

                                            {/* Prepaid / Partial Box */}
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-sky-500/10 border-sky-500/20' : 'bg-sky-50 border-sky-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-sky-400/60' : 'text-sky-600/60'}`}>Prepaid / Partial</div>
                                                <div className={`text-6xl font-black tracking-tighter mb-3 ${isDark ? 'text-sky-400' : 'text-sky-600'}`}><Counter value={stats.monthly.partial.count} /></div>
                                                <div className={`text-xl font-black ${isDark ? 'text-sky-300' : 'text-sky-500'}`}>{formatCurrency(stats.monthly.partial.amount)}</div>
                                            </div>

                                            {/* Delivered MTD Box */}
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-emerald-400/60' : 'text-emerald-600/60'}`}>Delivered Orders</div>
                                                <div className={`text-6xl font-black tracking-tighter mb-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}><Counter value={stats.monthly.status.delivered} /></div>
                                                <div className={`text-xl font-black ${isDark ? 'text-emerald-300' : 'text-emerald-500'}`}>{formatCurrency(stats.monthly.status.deliveredAmt)}</div>
                                            </div>
                                        </div>

                                        <div className={`p-4 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className={`text-[10px] font-black uppercase tracking-[0.3em] mb-3 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>MTD Status Breakdown</div>
                                            <div className="flex flex-wrap gap-2 max-h-40 overflow-auto custom-scrollbar">
                                                {Object.entries(stats.monthly.statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                                                    <div key={status} className={`px-3 py-1.5 rounded-xl border ${isDark ? 'bg-[#010103]/50 border-white/5' : 'bg-white border-slate-200'} flex items-center gap-3`}>
                                                        <span className={`text-[10px] font-black uppercase tracking-widest ${getStatusColor(status)}`}>{status}</span>
                                                        <span className={`text-sm font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* TODAY Card */}
                                <motion.div className={`${isDark ? GLASS_CARD_DARK : GLASS_CARD_LIGHT} p-12 flex flex-col relative overflow-hidden`}>
                                    <div className="absolute top-0 left-0 w-2 h-full bg-amber-500" />
                                    <div className="flex justify-between items-start mb-10 z-10">
                                        <div>
                                            <h2 className={`text-6xl font-black uppercase tracking-tighter italic ${isDark ? 'text-white' : 'text-slate-900'}`}>TODAY <span className="text-amber-500 not-italic">Pulse</span></h2>
                                            <p className={`text-sm font-black uppercase tracking-[0.4em] mt-3 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                                                {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'long' })}
                                            </p>
                                        </div>
                                        <div className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                                            <FiZap size={40} className="text-amber-500" />
                                        </div>
                                    </div>

                                    <div className="flex-1 flex flex-col justify-center gap-6 z-10">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>Total Orders (All)</div>
                                                <div className={`text-5xl font-black tracking-tighter mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><Counter value={stats.daily.totalAll} /></div>
                                                <div className="text-xl font-black text-amber-400">{formatCurrency(stats.daily.amountAll)}</div>
                                            </div>
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-sky-500/10 border-sky-500/20' : 'bg-sky-50 border-sky-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-sky-400/60' : 'text-sky-600/60'}`}>Prepaid / Partial</div>
                                                <div className="text-5xl font-black text-sky-400 tracking-tighter mb-2"><Counter value={stats.daily.partial.count} /></div>
                                                <div className={`text-xl font-black ${isDark ? 'text-sky-400/70' : 'text-sky-600/70'}`}>{formatCurrency(stats.daily.partial.amount)}</div>
                                            </div>
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-rose-500/10 border-rose-500/20' : 'bg-rose-50 border-rose-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-rose-400/60' : 'text-rose-600/60'}`}>Cash on Delivery</div>
                                                <div className="text-5xl font-black text-rose-400 tracking-tighter mb-2"><Counter value={stats.daily.cod.count} /></div>
                                                <div className={`text-xl font-black ${isDark ? 'text-rose-400/70' : 'text-rose-600/70'}`}>{formatCurrency(stats.daily.cod.amount)}</div>
                                            </div>
                                            <div className={`p-6 rounded-3xl border ${isDark ? 'bg-zinc-500/10 border-zinc-500/20' : 'bg-zinc-50 border-zinc-200'}`}>
                                                <div className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-zinc-400/60' : 'text-zinc-600/60'}`}>Canceled Orders</div>
                                                <div className="text-5xl font-black text-zinc-400 tracking-tighter mb-2"><Counter value={stats.daily.canceled.count} /></div>
                                                <div className={`text-xl font-black ${isDark ? 'text-zinc-400/70' : 'text-zinc-600/70'}`}>{formatCurrency(stats.daily.canceled.amount)}</div>
                                            </div>
                                        </div>

                                        <div className={`p-4 rounded-3xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                                            <div className={`text-[10px] font-black uppercase tracking-[0.3em] mb-3 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>TODAY Status Breakdown</div>
                                            <div className="flex flex-wrap gap-2 max-h-24 overflow-auto custom-scrollbar">
                                                {Object.entries(stats.daily.statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => (
                                                    <div key={status} className={`px-3 py-1.5 rounded-xl border ${isDark ? 'bg-[#010103]/50 border-white/5' : 'bg-white border-slate-200'} flex items-center gap-3`}>
                                                        <span className={`text-[10px] font-black uppercase tracking-widest ${getStatusColor(status)}`}>{status}</span>
                                                        <span className={`text-sm font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>{count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}

                        {slide === "BATTLE" && (
                            <motion.div key="battle" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.2 }} className="grid grid-cols-2 gap-16 h-full items-center">
                                {FLOOR_HEADS.map((head) => (
                                    <FloorHeadCard
                                        key={head.key}
                                        head={head}
                                        stats={floorStats[head.key]}
                                        isDark={isDark}
                                    />
                                ))}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
                                    <div className={`font-black text-6xl p-10 rounded-full shadow-[0_0_80px_rgba(99,102,241,0.3)] border-[10px] italic flex items-center justify-center w-40 h-40 ${isDark ? "bg-[#010103] border-white/10 text-white shadow-indigo-500/40" : "bg-white border-slate-200 text-slate-900 shadow-slate-200"}`}>
                                        VS
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {(slide.includes("TOP") || slide.includes("LOW")) && (
                            <motion.div key={slide} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} className="h-full flex flex-col">
                                <div className={`flex justify-between items-center mb-8 p-6 rounded-3xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-100 border-slate-200'}`}>
                                    <div className="flex items-center gap-6">
                                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl ${slide.includes("TOP") ? "bg-emerald-500 shadow-emerald-500/20" : "bg-rose-500 shadow-rose-500/20"}`}>
                                            {slide.includes("TOP") ? <FiAward size={32} className="text-white" /> : <FiAlertCircle size={32} className="text-white animate-pulse" />}
                                        </div>
                                        <div>
                                            <h2 className="text-5xl font-black tracking-tighter italic uppercase flex items-center gap-4">
                                                <span className={slide.includes("RAHUL") ? "text-amber-400" : "text-sky-400"}>{slide.includes("RAHUL") ? "RAHUL" : "ANAND"} SQUAD</span>
                                                <span className={isDark ? "text-white/20" : "text-slate-300"}>/</span>
                                                <span className={slide.includes("TOP") ? "text-emerald-400" : "text-rose-400"}>
                                                    {slide.includes("TOP") ? "High Performance Elite" : "Critical Performance Re-Alignment"}
                                                </span>
                                            </h2>
                                            <p className={`${isDark ? "text-white/30" : "text-slate-400"} text-xs font-black uppercase tracking-[0.4em] mt-1`}>
                                                {slide.includes("TOP") ? "Top 20 Operators driving Nexus growth" : "Bottom 20 Operators requiring management intervention"}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`px-8 py-3 rounded-2xl border font-black text-2xl italic ${slide.includes("TOP") ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-rose-500/10 text-rose-400 border-rose-500/20"}`}>
                                        {slide.includes("TOP") ? "RANK 1 - 20" : "RANK 21 - 40"}
                                    </div>
                                </div>

                                <div className="grid grid-cols-4 gap-4 flex-1 overflow-hidden">
                                    {(slide.includes("RAHUL") ?
                                        (slide.includes("TOP") ? agentRankings.RAHUL.top : agentRankings.RAHUL.low) :
                                        (slide.includes("TOP") ? agentRankings.ANAND.top : agentRankings.ANAND.low)
                                    ).map((a, i) => (
                                        <AgentRankCard key={a.name} agent={a} rank={slide.includes("TOP") ? i + 1 : i + 21} isLow={slide.includes("LOW")} isDark={isDark} />
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {slide === "MATRIX" && (
                            <motion.div key="floor_matrix" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex gap-8">
                                {/* Rahul Floor */}
                                <div className="flex-1 flex flex-col h-full">
                                    <div className={`border p-6 rounded-3xl mb-6 flex justify-between items-center relative overflow-hidden ${isDark ? "bg-amber-400/5 border-amber-400/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" : "bg-white border-slate-200 shadow-sm"}`}>
                                        {isDark && <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 blur-3xl -mr-16 -mt-16" />}
                                        <div className="relative z-10">
                                            <h3 className={`text-3xl font-black italic leading-none ${isDark ? "text-amber-400" : "text-amber-600"}`}>RAHUL FLOOR</h3>
                                            <p className={`${isDark ? "text-white/20" : "text-slate-400"} text-[10px] font-black uppercase tracking-[0.4em] mt-1`}>GMIT Operational Hub</p>
                                        </div>
                                        <div className="text-right relative z-10">
                                            <div className={`text-3xl font-black italic ${isDark ? "text-amber-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.2)]" : "text-amber-600"}`}><Counter value={floorStats.GMIT.daily.orders} /></div>
                                            <div className={`${isDark ? "text-white/20" : "text-slate-400"} text-[8px] font-black uppercase tracking-widest`}>Floor Total Today</div>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-4 space-y-4">
                                        {FLOOR_MANAGERS.GMIT.map((k, i) => (
                                            <ManagerCompactCard
                                                key={k}
                                                name={k}
                                                daily={stats.daily.managers[k] || { orders: 0, sales: 0 }}
                                                monthly={stats.monthly.managers[k] || { orders: 0, sales: 0 }}
                                                delay={i * 0.1}
                                                color="amber"
                                                isDark={isDark}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Vertical Divider */}
                                <div className={`w-px h-full relative ${isDark ? "bg-white/5" : "bg-slate-200"}`}>
                                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 py-8 font-black text-xs vertical-text tracking-[1em] ${isDark ? "bg-[#010103] text-white/10" : "bg-slate-50 text-slate-300"}`}>NEXUS</div>
                                </div>

                                {/* Anand Floor */}
                                <div className="flex-1 flex flex-col h-full">
                                    <div className={`border p-6 rounded-3xl mb-6 flex justify-between items-center relative overflow-hidden ${isDark ? "bg-sky-400/5 border-sky-400/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" : "bg-white border-slate-200 shadow-sm"}`}>
                                        {isDark && <div className="absolute top-0 right-0 w-32 h-32 bg-sky-400/10 blur-3xl -mr-16 -mt-16" />}
                                        <div className="relative z-10">
                                            <h3 className={`text-3xl font-black italic leading-none ${isDark ? "text-sky-400" : "text-sky-600"}`}>ANAND FLOOR</h3>
                                            <p className={`${isDark ? "text-white/20" : "text-slate-400"} text-[10px] font-black uppercase tracking-[0.4em] mt-1`}>ALTF Operational Hub</p>
                                        </div>
                                        <div className="text-right relative z-10">
                                            <div className={`text-3xl font-black italic ${isDark ? "text-sky-400 drop-shadow-[0_0_10px_rgba(56,189,248,0.2)]" : "text-sky-600"}`}><Counter value={floorStats.ALTF.daily.orders} /></div>
                                            <div className={`${isDark ? "text-white/20" : "text-slate-400"} text-[8px] font-black uppercase tracking-widest`}>Floor Total Today</div>
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto custom-scrollbar pr-4 space-y-4">
                                        {FLOOR_MANAGERS.ALTF.map((k, i) => (
                                            <ManagerCompactCard
                                                key={k}
                                                name={k}
                                                daily={stats.daily.managers[k] || { orders: 0, sales: 0 }}
                                                monthly={stats.monthly.managers[k] || { orders: 0, sales: 0 }}
                                                delay={i * 0.1}
                                                color="sky"
                                                isDark={isDark}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Global HUD Nav */}
                <footer className={`mt-6 flex justify-between items-center px-6 border-t pt-6 ${isDark ? 'border-white/5 shadow-[0_-1px_15px_rgba(0,0,0,0.2)]' : 'border-slate-200 shadow-sm'}`}>
                    <div className="flex items-center gap-8">
                        <div className="flex gap-6">
                            {[
                                { id: "SUMMARY", label: "HUD", icon: FiActivity },
                                { id: "BATTLE", label: "WAR", icon: FiShield },
                                { id: "RAHUL", label: "RAHUL", icon: FiUser },
                                { id: "ANAND", label: "ANAND", icon: FiUser },
                                { id: "MATRIX", label: "COMMAND", icon: FiTrendingUp }
                            ].map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => setSlide(s.id === "RAHUL" ? "RAHUL_TOP" : s.id === "ANAND" ? "ANAND_TOP" : s.id)}
                                    className={`flex items-center gap-3 px-8 py-3 rounded-2xl transition-all duration-700 relative group shadow-lg ${slide.includes(s.id) ? (isDark ? "bg-white text-[#010103] shadow-white/10" : "bg-slate-900 text-white shadow-slate-900/20") : (isDark ? "bg-white/5 text-white/30 hover:bg-white/10 shadow-inner" : "bg-slate-100 text-slate-400 hover:bg-slate-200 shadow-inner")
                                        }`}
                                >
                                    <s.icon size={20} />
                                    <span className="font-black text-xs uppercase tracking-widest">{s.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className={`flex items-center gap-4 px-6 py-2 border rounded-xl shadow-inner ${isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'}`}>
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
                            <span className="text-emerald-400 font-black text-[10px] uppercase tracking-widest">Live Sync Active</span>
                            <span className={`${isDark ? 'text-white/20' : 'text-slate-400'} text-[10px] font-black ml-4`}>
                                LAST UPDATED: {lastSyncTime}
                            </span>
                        </div>
                    </div>

                    <div className="flex items-center gap-8">
                        <div className="flex flex-col items-end">
                            <span className={`text-4xl font-black font-mono tracking-tighter px-4 py-1 rounded-lg border shadow-xl ${isDark ? 'text-white/90 bg-[#0a0a0c] border-white/10 shadow-indigo-500/5' : 'text-slate-900 bg-white border-slate-200 shadow-slate-200'}`}>
                                {new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                        </div>
                        <div className={`h-12 w-px ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
                        <div className="flex flex-col items-end">
                            <span className="text-indigo-500 font-black text-lg leading-none italic drop-shadow-sm">SUWASTHI NEXUS</span>
                            <span className={`${isDark ? 'text-white/20' : 'text-slate-400'} text-[8px] font-black tracking-[0.8em] mt-1 uppercase`}>Prepaid Network</span>
                        </div>
                    </div>
                </footer>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;900&display=swap');
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.01); }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 20px; }
      `}} />
        </div>
    );
}